import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMediaQuery } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, Typography, Box, Divider } from '@mui/material';

export const UserWorkoutsStatistics = ({ cardioStatistics, strengthStatistics }) => {
  const isMobile = useMediaQuery('(max-width:768px)');

  return (
    <div>
      {/* Cardio Statistics Section */}
      <Paper style={{ marginBottom: '20px', padding: '16px' }}>
        <Typography variant="h6" gutterBottom>
          <FormattedMessage id="userWorkouts.cardio.title" defaultMessage="Cardio Statistics" />
        </Typography>

        {isMobile ? (
          // Mobile View - Blocks
          <Box>
            {cardioStatistics.map((stat) => (
              <Paper key={stat.workoutName} style={{ marginBottom: '12px', padding: '12px' }}>
                <Typography variant="subtitle1">{stat.workoutName}</Typography>
                <Divider />
                <Typography>Weekly Distance: {stat.weeklyDistanceKm} km</Typography>
                <Typography>Weekly Time: {stat.weeklyTimeMinutes} minutes</Typography>
                <Typography>Weekly Calories: {stat.weeklyCaloriesBurned}</Typography>
                <Typography>Monthly Distance: {stat.monthlyDistanceKm} km</Typography>
                <Typography>Monthly Time: {stat.monthlyTimeMinutes} minutes</Typography>
                <Typography>Monthly Calories: {stat.monthlyCaloriesBurned}</Typography>
                <Typography>Yearly Distance: {stat.yearlyDistanceKm} km</Typography>
                <Typography>Yearly Time: {stat.yearlyTimeMinutes} minutes</Typography>
                <Typography>Yearly Calories: {stat.yearlyCaloriesBurned}</Typography>
                <Typography>All-Time Distance: {stat.allTimeDistanceKm} km</Typography>
                <Typography>All-Time Time: {stat.allTimeTimeMinutes} minutes</Typography>
                <Typography>All-Time Calories: {stat.allTimeCaloriesBurned}</Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          // Desktop View - Table
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Workout Name</TableCell>
                <TableCell>Weekly Distance (km)</TableCell>
                <TableCell>Weekly Time (minutes)</TableCell>
                <TableCell>Weekly Calories Burned</TableCell>
                <TableCell>Monthly Distance (km)</TableCell>
                <TableCell>Monthly Time (minutes)</TableCell>
                <TableCell>Monthly Calories Burned</TableCell>
                <TableCell>Yearly Distance (km)</TableCell>
                <TableCell>Yearly Time (minutes)</TableCell>
                <TableCell>Yearly Calories Burned</TableCell>
                <TableCell>All-Time Distance (km)</TableCell>
                <TableCell>All-Time Time (minutes)</TableCell>
                <TableCell>All-Time Calories Burned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cardioStatistics.map((stat) => (
                <TableRow key={stat.workoutName}>
                  <TableCell>{stat.workoutName}</TableCell>
                  <TableCell>{stat.weeklyDistanceKm}</TableCell>
                  <TableCell>{stat.weeklyTimeMinutes}</TableCell>
                  <TableCell>{stat.weeklyCaloriesBurned}</TableCell>
                  <TableCell>{stat.monthlyDistanceKm}</TableCell>
                  <TableCell>{stat.monthlyTimeMinutes}</TableCell>
                  <TableCell>{stat.monthlyCaloriesBurned}</TableCell>
                  <TableCell>{stat.yearlyDistanceKm}</TableCell>
                  <TableCell>{stat.yearlyTimeMinutes}</TableCell>
                  <TableCell>{stat.yearlyCaloriesBurned}</TableCell>
                  <TableCell>{stat.allTimeDistanceKm}</TableCell>
                  <TableCell>{stat.allTimeTimeMinutes}</TableCell>
                  <TableCell>{stat.allTimeCaloriesBurned}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Strength Statistics Section */}
      <Paper style={{ padding: '16px' }}>
        <Typography variant="h6" gutterBottom>
          <FormattedMessage id="userWorkouts.strength.title" defaultMessage="Strength Statistics" />
        </Typography>

        {isMobile ? (
          // Mobile View - Blocks
          <Box>
            {strengthStatistics.map((stat) => (
              <Paper key={stat.workoutName} style={{ marginBottom: '12px', padding: '12px' }}>
                <Typography variant="subtitle1">{stat.workoutName}</Typography>
                <Divider />
                <Typography>Weekly Repetitions: {stat.totalWeeklyRepetitions}</Typography>
                <Typography>Weekly Weight: {stat.totalWeeklyWeight} kg</Typography>
                <Typography>Monthly Repetitions: {stat.totalMonthlyRepetitions}</Typography>
                <Typography>Monthly Weight: {stat.totalMonthlyWeight} kg</Typography>
                <Typography>Yearly Repetitions: {stat.totalYearlyRepetitions}</Typography>
                <Typography>Yearly Weight: {stat.totalYearlyWeight} kg</Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          // Desktop View - Table
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Workout Name</TableCell>
                <TableCell>Weekly Repetitions</TableCell>
                <TableCell>Weekly Weight (kg)</TableCell>
                <TableCell>Monthly Repetitions</TableCell>
                <TableCell>Monthly Weight (kg)</TableCell>
                <TableCell>Yearly Repetitions</TableCell>
                <TableCell>Yearly Weight (kg)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strengthStatistics.map((stat) => (
                <TableRow key={stat.workoutName}>
                  <TableCell>{stat.workoutName}</TableCell>
                  <TableCell>{stat.totalWeeklyRepetitions}</TableCell>
                  <TableCell>{stat.totalWeeklyWeight}</TableCell>
                  <TableCell>{stat.totalMonthlyRepetitions}</TableCell>
                  <TableCell>{stat.totalMonthlyWeight}</TableCell>
                  <TableCell>{stat.totalYearlyRepetitions}</TableCell>
                  <TableCell>{stat.totalYearlyWeight}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

UserWorkoutsStatistics.propTypes = {
  cardioStatistics: PropTypes.arrayOf(
    PropTypes.shape({
      workoutName: PropTypes.string.isRequired,
      weeklyDistanceKm: PropTypes.number.isRequired,
      weeklyTimeMinutes: PropTypes.number.isRequired,
      weeklyCaloriesBurned: PropTypes.number.isRequired,
      monthlyDistanceKm: PropTypes.number.isRequired,
      monthlyTimeMinutes: PropTypes.number.isRequired,
      monthlyCaloriesBurned: PropTypes.number.isRequired,
      yearlyDistanceKm: PropTypes.number.isRequired,
      yearlyTimeMinutes: PropTypes.number.isRequired,
      yearlyCaloriesBurned: PropTypes.number.isRequired,
      allTimeDistanceKm: PropTypes.number.isRequired,
      allTimeTimeMinutes: PropTypes.number.isRequired,
      allTimeCaloriesBurned: PropTypes.number.isRequired,
    })
  ).isRequired,
  strengthStatistics: PropTypes.arrayOf(
    PropTypes.shape({
      workoutName: PropTypes.string.isRequired,
      totalWeeklyRepetitions: PropTypes.number.isRequired,
      totalWeeklyWeight: PropTypes.number.isRequired,
      totalMonthlyRepetitions: PropTypes.number.isRequired,
      totalMonthlyWeight: PropTypes.number.isRequired,
      totalYearlyRepetitions: PropTypes.number.isRequired,
      totalYearlyWeight: PropTypes.number.isRequired,
    })
  ).isRequired,
};
